import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import { DataTable, EmptyState, SectionCard, AuditLogIcon } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../portfolio_definition/HelpPanel';
import { api, type AuditEntry } from '../../api';
import { describeAuditEntry } from './describeAudit';
import { useAsyncData } from './useAsyncData';

/** Local yyyy-mm-dd — `toISOString` would shift the day for anyone behind UTC. */
function toDateInput(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateInput(date);
}

type Preset = 'today' | '7d' | '30d' | 'all';

const PRESET_RANGES: Record<Preset, () => { from: string; to: string }> = {
  today: () => ({ from: toDateInput(new Date()), to: toDateInput(new Date()) }),
  '7d': () => ({ from: daysAgo(7), to: '' }),
  '30d': () => ({ from: daysAgo(30), to: '' }),
  all: () => ({ from: '', to: '' }),
};

/** Field height, so the date inputs and the Apply button share one baseline. */
const CONTROL_HEIGHT = 40;

interface DescribedRow extends AuditEntry {
  summary: string;
  detail: string;
}

/**
 * The drawer answers the question the screen cannot: what is NOT in here.
 *
 * An audit log invites the assumption that it records everything, and a client making a
 * compliance claim on that basis would be wrong. It records administrative CHANGES. It is
 * not a sign-in log, not a read log, and not a record of work done inside the products.
 */
const AUDIT_LOG_HELP: HelpContent = {
  title: 'Audit Log',
  icon: '📜',
  purpose:
    'The record of <strong>every administrative change made in this install</strong> &mdash; what changed, who changed it and when. Read-only by construction: there is no endpoint in the platform that edits or deletes an entry.',
  doHere: [
    'Narrow the range with <strong>Today</strong>, <strong>7 days</strong>, <strong>30 days</strong> or <strong>All time</strong>.',
    'Or set an explicit <strong>From</strong> and <strong>To</strong> date and press <strong>Apply</strong> &mdash; the query runs when you apply it, not as you type.',
    'Read <strong>What happened</strong> for the change and <strong>Details</strong> for the specifics. Hover a truncated <em>Details</em> cell to see it in full.',
    'Sort by <strong>When</strong> or <strong>Who</strong>, and page through with the control at the foot of the table.',
  ],
  fits: 'Administration &rarr; Audit Log',
  firstTime:
    'Start on <strong>All time</strong> &mdash; an install that is only lightly administered may have nothing in the last 7 days, and an empty table on a narrow range reads as "nothing is being recorded" when it only means "nothing happened that week".',
  sections: [
    {
      type: 'defs',
      label: 'The four columns',
      items: [
        {
          term: 'When',
          def: 'The moment the change was committed, shown in <strong>your own timezone</strong>, not the server&rsquo;s. Two administrators in different countries see the same event at different clock times, and both are correct.',
        },
        {
          term: 'Who',
          def: 'The account that made the change. <strong>System</strong> means no administrator was signed in &mdash; the platform acted on its own, as it does when it provisions the very first administrator on an install that has no users yet.',
        },
        {
          term: 'What happened',
          def: 'The change as a sentence. The stored row is a raw action key with the before and after values; it is rendered into English for this screen, so the record stays complete while the screen stays readable.',
        },
        {
          term: 'Details',
          def: 'The specifics &mdash; which module, which user, which value changed from what to what. Truncated to one line to keep rows scannable; the full text is in the cell&rsquo;s tooltip.',
        },
      ],
    },
    {
      type: 'defs',
      label: 'What is recorded',
      items: [
        {
          term: 'Users',
          def: 'Accounts created and edited, module access changed, accounts locked and unlocked, passwords reset.',
        },
        {
          term: 'Licensing',
          def: 'A module licensed or un-licensed, and seat counts changed.',
        },
        {
          term: 'Company Profile',
          def: 'Every field edited on the company record, with its previous value.',
        },
        {
          term: 'SSO / Identity',
          def: 'Provider toggles, tenant and domain restrictions, and <em>that</em> a client secret was replaced.',
        },
        {
          term: 'Open AI Integration',
          def: 'A provider&rsquo;s connection settings, the application-wide default provider, and any per-module override &mdash; each as its own entry, with what it was before.',
        },
      ],
    },
    {
      type: 'html',
      label: 'What is not recorded here',
      html: '<strong>Not a sign-in log</strong> &mdash; successful and failed logins are not entries on this screen. <strong>Not a read log</strong> &mdash; nothing records that somebody opened a screen or exported a report. And <strong>work inside the products is not here</strong>: editing an application in APR, or a capability model in ERP Consolidation, is that module&rsquo;s own activity and belongs to that module, not to this administrative record.',
    },
    {
      type: 'html',
      label: 'Secrets never appear in an entry',
      html: 'Where a change involves a credential &mdash; an SSO client secret, an AI provider API key &mdash; the entry records <strong>that it was replaced or removed, never its value</strong>. So reading this log can tell you when a key last changed and who changed it, and can never tell you what the key is.',
    },
    {
      type: 'html',
      label: 'How much is shown at once',
      html: 'A query returns <strong>at most 500 entries</strong>, newest first. On a busy install a wide range can therefore be a window on the most recent activity rather than the whole period &mdash; narrow the dates to reach further back with confidence that nothing in between was cut off.',
    },
  ],
};

/**
 * Audit log viewer.
 *
 * Spec: every mutating admin action is logged, "immutable, queryable in the admin
 * UI". Read-only by construction — no endpoint edits or deletes an entry.
 *
 * The stored rows are raw (action keys and JSON); `describeAuditEntry` renders them
 * as sentences, so the record stays complete while the screen stays readable.
 */
export function AuditLogTab() {
  const [preset, setPreset] = useState<Preset | null>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // Applied separately from the inputs so typing a date does not fire a request per
  // keystroke — the query runs when the user commits the range.
  const [applied, setApplied] = useState<{ from: string; to: string }>({ from: '', to: '' });

  const { data, loading, error } = useAsyncData(
    () => api.admin.getAudit({ limit: 500, from: applied.from, to: applied.to }),
    [applied.from, applied.to],
  );

  const rows = useMemo<DescribedRow[]>(
    () => (data?.entries ?? []).map((entry) => ({ ...entry, ...describeAuditEntry(entry) })),
    [data],
  );

  const choosePreset = (next: Preset | null) => {
    if (!next) return;
    const range = PRESET_RANGES[next]();
    setPreset(next);
    setFrom(range.from);
    setTo(range.to);
    setApplied(range);
  };

  // Editing a date by hand means the range is no longer one of the presets.
  const editFrom = (value: string) => {
    setFrom(value);
    setPreset(null);
  };
  const editTo = (value: string) => {
    setTo(value);
    setPreset(null);
  };

  const rangeLabel =
    !applied.from && !applied.to
      ? 'All activity, newest first.'
      : `${applied.from || 'Earliest'} to ${applied.to || 'now'}, newest first.`;

  const columns: GridColDef<DescribedRow>[] = [
    {
      field: 'timestamp',
      headerName: 'When',
      width: 170,
      valueFormatter: (value) =>
        new Date(value as string).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }),
    },
    {
      field: 'actor_name',
      headerName: 'Who',
      width: 180,
      valueGetter: (value) => value ?? 'System',
    },
    {
      field: 'summary',
      headerName: 'What happened',
      flex: 1.1,
      minWidth: 260,
      sortable: false,
    },
    {
      field: 'detail',
      headerName: 'Details',
      flex: 1.4,
      minWidth: 280,
      sortable: false,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color="text.secondary"
          title={params.value as string}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {params.value as string}
        </Typography>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <HelpPanel help={AUDIT_LOG_HELP} />

      {error ? <Alert severity="error">{error}</Alert> : null}

      <SectionCard title={
          <Stack direction="row" spacing={1} alignItems="center">
            <AuditLogIcon fontSize="small" />
            <span>Audit log</span>
          </Stack>
        } subheader={rangeLabel} disableContentPadding>
        {/* Filter toolbar: presets on the left, explicit range on the right, one
            shared baseline. No helper text under a single field — that is what
            pushed the two date inputs out of alignment. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            px: 2.5,
            py: 2,
          }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preset}
            onChange={(_, next: Preset | null) => choosePreset(next)}
            aria-label="Date range preset"
            sx={{ height: CONTROL_HEIGHT }}
          >
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="7d">7 days</ToggleButton>
            <ToggleButton value="30d">30 days</ToggleButton>
            <ToggleButton value="all">All time</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flex: 1, minWidth: 8 }} />

          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              label="From"
              type="date"
              size="small"
              value={from}
              onChange={(e) => editFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 172 }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={to}
              onChange={(e) => editTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 172 }}
            />
            <Button
              variant="outlined"
              onClick={() => setApplied({ from, to })}
              sx={{ height: CONTROL_HEIGHT }}
            >
              Apply
            </Button>
          </Stack>
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ p: 6, display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No activity in this date range"
            description="Widen the range, or choose All time to see the full history."
          />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        )}
      </SectionCard>
    </Stack>
  );
}
